import {
  JSNS,
  type JsonObject,
  type ActivityInfo,
  type CreateWaitSetResult,
  type ModifiedItemsIds,
  type NoOpOptions,
  type NoOpResult,
  type QueuedNotifications,
  type ReminderDevice,
  type SyncResult,
  type WaitSetAccount,
  type WaitSetResult,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

/** Options for the NoOp long-poll loop. */
export interface NotificationLoopOptions {
  /** suggested server block duration per NoOp, ms (server clamps). Default 300000. */
  timeout?: number;
  /** also surface delegated/shared-mailbox changes. Default true. */
  includeDelegates?: boolean;
  /**
   * Inter-poll delay function. Resolves after `ms`; injectable so tests drive
   * the loop WITHOUT real timers. Default: a setTimeout-based wait.
   */
  delay?: (ms: number) => Promise<void>;
  /** delay (ms) before re-issuing after a NoOp resolves normally. Default 0. */
  pollDelay?: number;
  /** backoff (ms) before retrying after a NoOp error. Default 5000. */
  errorBackoff?: number;
  /** surfaced loop errors (network/SOAP). The loop continues after backoff. */
  onError?: (err: unknown) => void;
}

/** Handle returned by startNotificationLoop. */
export interface NotificationLoop {
  /** stop the loop; no further NoOp is issued after the in-flight one settles */
  stop(): void;
  /** has stop() been called? */
  readonly stopped: boolean;
  /** resolves when the loop has fully wound down (in-flight NoOp settled) */
  readonly done: Promise<void>;
}

const defaultDelay = (ms: number): Promise<void> =>
  ms <= 0 ? Promise.resolve() : new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Real-time sync & notifications (urn:zimbraMail), plus reminder-device mgmt.
 *
 * The notify channel itself is parsed centrally off every session response by
 * the client (NotificationHub). This domain provides the COMMANDS that drive it
 * — most importantly the NoOp long-poll loop (`startNotificationLoop`), which is
 * the heartbeat of auto-refresh for a single-user webmail client — plus the
 * WaitSet (multi-mailbox) and Sync (delta reconciliation) models and helpers.
 */
export class SyncApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ---------------- Model 1: NoOp long-poll ---------------- */

  /**
   * NoOp (urn:zimbraMail) — the "hanging GET" long-poll. With `wait` it blocks
   * server-side up to `timeout`, returning as soon as something changes; the
   * `<notify>` block rides on the response header and is dispatched centrally by
   * the client. An empty/timed-out response is normal — just re-issue.
   */
  async noop(opts?: NoOpOptions): Promise<NoOpResult> {
    const params: JsonObject = {};
    if (opts?.wait !== undefined) params.wait = opts.wait;
    if (opts?.includeDelegates !== undefined) params.includeDelegates = opts.includeDelegates;
    if (opts?.enforceLimit !== undefined) params.enforceLimit = opts.enforceLimit;
    if (opts?.timeout !== undefined) params.timeout = opts.timeout;
    return this.client.call<NoOpResult>(JSNS.mail, 'NoOp', params);
  }

  /**
   * Start the NoOp(wait=1) long-poll loop. Each iteration awaits a NoOp then
   * reschedules; `stop()` cancels (no further NoOp after the in-flight settles).
   * Notifications are emitted via `client.notifications` (the client threads the
   * ack seq automatically on the next request). Errors are surfaced via
   * `onError` and the loop backs off and resumes — no setInterval, no leak.
   */
  startNotificationLoop(opts?: NotificationLoopOptions): NotificationLoop {
    const delay = opts?.delay ?? defaultDelay;
    const timeout = opts?.timeout ?? 300_000;
    const includeDelegates = opts?.includeDelegates ?? true;
    const pollDelay = opts?.pollDelay ?? 0;
    const errorBackoff = opts?.errorBackoff ?? 5_000;

    let stopped = false;

    const run = async (): Promise<void> => {
      while (!stopped) {
        try {
          await this.noop({ wait: true, includeDelegates, timeout });
          if (stopped) break;
          if (pollDelay > 0) await delay(pollDelay);
        } catch (err) {
          if (opts?.onError) opts.onError(err);
          if (stopped) break;
          await delay(errorBackoff);
        }
      }
    };

    const done = run();

    return {
      stop(): void {
        stopped = true;
      },
      get stopped(): boolean {
        return stopped;
      },
      done,
    };
  }

  /* ---------------- Model 2: WaitSet ---------------- */

  /**
   * CreateWaitSet (urn:zimbraMail) — server-side watcher over one or more
   * accounts. `seq` in the result is an OPAQUE string — never parse as a number.
   */
  async createWaitSet(opts: {
    defTypes: string;
    add?: WaitSetAccount[];
    allAccounts?: boolean;
  }): Promise<CreateWaitSetResult> {
    const params: JsonObject = { defTypes: opts.defTypes };
    if (opts.allAccounts !== undefined) params.allAccounts = opts.allAccounts;
    if (opts.add?.length) params.add = { a: opts.add };
    return this.client.call<CreateWaitSetResult>(JSNS.mail, 'CreateWaitSet', params);
  }

  /**
   * WaitSet (urn:zimbraMail) — block (with `block`) until a watched account has
   * data, returning one `<n>` per changed account. Echo the highest `seq`
   * received (opaque). An empty response on timeout is normal — re-issue.
   */
  async waitSet(
    waitSet: string,
    seq: string,
    opts?: {
      block?: boolean;
      timeout?: number;
      add?: WaitSetAccount[];
      update?: WaitSetAccount[];
      remove?: Array<{ id: string }>;
    },
  ): Promise<WaitSetResult> {
    const params: JsonObject = { waitSet, seq };
    if (opts?.block !== undefined) params.block = opts.block;
    if (opts?.timeout !== undefined) params.timeout = opts.timeout;
    if (opts?.add?.length) params.add = { a: opts.add };
    if (opts?.update?.length) params.update = { a: opts.update };
    if (opts?.remove?.length) params.remove = { a: opts.remove };
    return this.client.call<WaitSetResult>(JSNS.mail, 'WaitSet', params);
  }

  /** DestroyWaitSet (urn:zimbraMail) — tear down a WaitSet. */
  async destroyWaitSet(waitSet: string): Promise<void> {
    await this.client.call(JSNS.mail, 'DestroyWaitSet', { waitSet });
  }

  /* ---------------- Model 3: Sync ---------------- */

  /**
   * Sync (urn:zimbraMail) — token-based full+delta reconciliation. No token =
   * initial sync (full tree); a token = delta. `more=1` ⇒ keep paging with the
   * returned token. A `mail.MUST_RESYNC` fault ⇒ re-initial-sync.
   */
  async sync(opts?: {
    token?: string;
    root?: string;
    typed?: boolean;
    deleteLimit?: number;
    changeLimit?: number;
    msgCutoff?: number;
    calCutoff?: number;
  }): Promise<SyncResult> {
    const params: JsonObject = {};
    if (opts?.token !== undefined) params.token = opts.token;
    if (opts?.root !== undefined) params.l = opts.root;
    if (opts?.typed !== undefined) params.typed = opts.typed;
    if (opts?.deleteLimit !== undefined) params.deleteLimit = opts.deleteLimit;
    if (opts?.changeLimit !== undefined) params.changeLimit = opts.changeLimit;
    if (opts?.msgCutoff !== undefined) params.msgCutoff = opts.msgCutoff;
    if (opts?.calCutoff !== undefined) params.calCutoff = opts.calCutoff;
    return this.client.call<SyncResult>(JSNS.mail, 'Sync', params);
  }

  /* ---------------- "what changed" helpers ---------------- */

  /**
   * GetModifiedItemsIDs (urn:zimbraMail) — just the modified/deleted ids in a
   * folder since a mod-sequence (lighter than a full Sync).
   */
  async getModifiedItemsIDs(folderId: string, modSeq: number): Promise<ModifiedItemsIds> {
    const res = await this.client.call<{
      mids?: number | number[] | { _content?: number };
      dids?: number | number[] | { _content?: number };
    }>(JSNS.mail, 'GetModifiedItemsIDs', { l: folderId, ms: modSeq });
    const ids = (v: typeof res.mids): number[] => {
      if (v === undefined) return [];
      if (Array.isArray(v)) return v;
      if (typeof v === 'number') return [v];
      return v._content !== undefined ? [v._content] : [];
    };
    return { modified: ids(res.mids), deleted: ids(res.dids) };
  }

  /** GetLastItemIdInMailbox (urn:zimbraMail) — high-water-mark item id. */
  async getLastItemId(): Promise<number> {
    const res = await this.client.call<{ id?: number | { _content?: number } }>(
      JSNS.mail,
      'GetLastItemIdInMailbox',
      {},
    );
    const id = res.id;
    return typeof id === 'number' ? id : (id?._content ?? 0);
  }

  /**
   * GetNotifications (urn:zimbraMail) — queued activity-style notifications
   * (shares / doc activity), independent of the session notify channel.
   */
  async getNotifications(): Promise<QueuedNotifications> {
    const res = await this.client.call<{
      lastSeen?: number;
      a?: ActivityInfo[];
    }>(JSNS.mail, 'GetNotifications', {});
    return {
      ...(res.lastSeen !== undefined ? { lastSeen: res.lastSeen } : {}),
      activities: res.a ?? [],
    };
  }

  /** GenerateUUID (urn:zimbraMail) — a server-generated unique id. */
  async generateUUID(): Promise<string> {
    const res = await this.client.call<{ uuid?: string | { _content?: string } }>(
      JSNS.mail,
      'GenerateUUID',
      {},
    );
    const uuid = res.uuid;
    return typeof uuid === 'string' ? uuid : (uuid?._content ?? '');
  }

  /** ResetRecentMessageCount (urn:zimbraMail) — reset the unseen-since-login count. */
  async resetRecentMessageCount(): Promise<void> {
    await this.client.call(JSNS.mail, 'ResetRecentMessageCount', {});
  }

  /* ---------------- G. Reminder-device management ---------------- */

  /** GetAllDevices (urn:zimbraMail) — list my reminder devices. */
  async getAllDevices(): Promise<ReminderDevice[]> {
    const res = await this.client.call<{ device?: ReminderDevice[] }>(
      JSNS.mail,
      'GetAllDevices',
      {},
    );
    return res.device ?? [];
  }

  /** RegisterDevice (urn:zimbraMail) — register a reminder device by name. */
  async registerDevice(name: string): Promise<{ id: string; name: string }> {
    const res = await this.client.call<{ device?: { id?: string; name?: string } }>(
      JSNS.mail,
      'RegisterDevice',
      { device: { name } },
    );
    return { id: res.device?.id ?? '', name: res.device?.name ?? name };
  }

  /** CheckDeviceStatus (urn:zimbraMail) — one device's status. */
  async checkDeviceStatus(id: string): Promise<{ id: string; status: string }> {
    const res = await this.client.call<{ device?: { id?: string; status?: string } }>(
      JSNS.mail,
      'CheckDeviceStatus',
      { device: { id } },
    );
    return { id: res.device?.id ?? id, status: res.device?.status ?? '' };
  }

  /** UpdateDeviceStatus (urn:zimbraMail) — update a device's status. */
  async updateDeviceStatus(id: string, status: string): Promise<void> {
    await this.client.call(JSNS.mail, 'UpdateDeviceStatus', { device: { id, status } });
  }

  /** DeleteDevice (urn:zimbraMail) — remove a reminder device. */
  async deleteDevice(id: string): Promise<void> {
    await this.client.call(JSNS.mail, 'DeleteDevice', { device: { id } });
  }

  /** InvalidateReminderDevice (urn:zimbraMail) — invalidate a reminder address. */
  async invalidateReminderDevice(address: string): Promise<void> {
    await this.client.call(JSNS.mail, 'InvalidateReminderDevice', { address });
  }
}
